import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Clock, Phone, ArrowRight } from "lucide-react";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { Button } from "../ui/button";
import { POSTS, getPostBySlug } from "../../lib/blog";
import { SITE } from "../../lib/site";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const Block = ({ block }) => {
  if (block.type === "h2")
    return (
      <h2 className="font-display text-2xl sm:text-3xl tracking-tight text-brand-ink mt-12 mb-4">
        {block.text}
      </h2>
    );
  if (block.type === "p")
    return (
      <p className="text-base md:text-lg text-brand-ink/80 leading-relaxed mt-4">
        {block.text}
      </p>
    );
  if (block.type === "ul")
    return (
      <ul className="mt-4 space-y-2 list-disc list-outside pl-5 text-base md:text-lg text-brand-ink/80 leading-relaxed">
        {block.items.map((it, i) => (
          <li key={i}>{it}</li>
        ))}
      </ul>
    );
  if (block.type === "callout")
    return (
      <aside className="mt-8 rounded-2xl bg-brand-sand border border-border p-6 text-base text-brand-ink/85 leading-relaxed">
        {block.text}
      </aside>
    );
  return null;
};

export const BlogPost = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  useEffect(() => {
    if (!post) return;
    document.title = `${post.title} — Empower Legacy Finance`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", post.description);
    window.scrollTo(0, 0);
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  const related = POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <main data-testid={`blog-post-${post.slug}`} className="font-body bg-brand-cream">
      <Header />

      <article className="pt-32 md:pt-40 pb-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Link
            to="/blog"
            data-testid="blog-back-link"
            className="inline-flex items-center gap-2 text-sm text-brand-eucalyptus hover:text-brand-terracottaDark mb-10"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.8} />
            All articles
          </Link>

          <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-brand-eucalyptus/80">
            <span>{post.category}</span>
            <span className="h-1 w-1 rounded-full bg-brand-ink/30" />
            <span className="flex items-center gap-1.5">
              <Clock className="h-3 w-3" strokeWidth={1.6} />
              {post.readMinutes} min read
            </span>
            <span className="h-1 w-1 rounded-full bg-brand-ink/30" />
            <time>{formatDate(post.publishedAt)}</time>
          </div>

          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium text-brand-ink leading-[1.05]">
            {post.title}
          </h1>
          <p className="mt-6 text-lg text-brand-ink/70 leading-relaxed">
            {post.description}
          </p>
        </div>

        <div className="max-w-5xl mx-auto px-6 lg:px-8 mt-12">
          <img
            src={post.image}
            alt={post.title}
            className="w-full aspect-[16/8] object-cover rounded-3xl border border-border"
          />
        </div>

        <div className="max-w-3xl mx-auto px-6 lg:px-8 mt-12">
          {post.blocks.map((b, i) => (
            <Block key={i} block={b} />
          ))}

          <div className="mt-16 rounded-3xl bg-brand-eucalyptus text-brand-cream p-8 sm:p-10 relative overflow-hidden">
            <div className="absolute inset-0 grain pointer-events-none" />
            <div className="relative">
              <div className="label-eyebrow text-brand-cream/70">Talk to a broker</div>
              <h3 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight">
                Want this advice tailored to your situation?
              </h3>
              <p className="mt-4 text-brand-cream/80 max-w-xl">
                A free 15-minute call. No obligation. We&apos;ll map out your
                options across 40+ Australian lenders.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <a href={SITE.phoneTel} data-testid={`blog-${post.slug}-call`}>
                  <Button className="rounded-full h-12 px-6 bg-brand-cream text-brand-eucalyptus hover:bg-brand-terracotta hover:text-brand-cream transition-all">
                    <Phone className="h-4 w-4 mr-2" strokeWidth={1.8} />
                    Call {SITE.phone}
                  </Button>
                </a>
                <Link to="/#contact" data-testid={`blog-${post.slug}-enquire`}>
                  <Button
                    variant="outline"
                    className="rounded-full h-12 px-6 border-brand-cream/40 text-brand-cream hover:bg-brand-cream hover:text-brand-terracotta transition-all"
                  >
                    Talk to a broker
                    <ArrowRight className="h-4 w-4 ml-2" strokeWidth={1.8} />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-32">
          <div className="label-eyebrow text-brand-eucalyptus mb-5">Keep reading</div>
          <div className="grid md:grid-cols-2 gap-6">
            {related.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`blog-related-${p.slug}`}
                className="group rounded-2xl overflow-hidden border border-border bg-card transition-all duration-300 hover:-translate-y-1 hover:shadow-md flex"
              >
                <div className="w-1/3 aspect-square overflow-hidden">
                  <img
                    src={p.image}
                    alt=""
                    aria-hidden
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="flex-1 p-6">
                  <div className="text-[11px] uppercase tracking-widest text-brand-eucalyptus/80">
                    {p.category}
                  </div>
                  <h4 className="mt-2 font-display text-lg tracking-tight text-brand-ink group-hover:text-brand-terracotta transition-colors">
                    {p.title}
                  </h4>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <Footer />
    </main>
  );
};
